import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';

export const useSpeechRecognition = () => {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  const initialize = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Reconnaissance vocale non supportée par ce navigateur');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SpeechRecognition();

    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'fr-FR';
    rec.maxAlternatives = 1;

    rec.onstart = () => {
      setIsListening(true);
      toast.success('Microphone activé - Parlez maintenant');
    };

    rec.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        toast.error("Permission microphone refusée. Veuillez autoriser l'accès au microphone.");
      } else if (event.error === 'no-speech') {
        toast.warning('Aucun discours détecté. Réessayez.');
      } else {
        toast.error(`Erreur reconnaissance vocale: ${event.error}`);
      }
    };

    rec.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = rec;
  }, []);

  const startListening = useCallback((onResult) => {
    const rec = recognitionRef.current;
    if (!rec || isListening) return;

    rec.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      const fullTranscript = finalTranscript || interimTranscript;
      if (fullTranscript && onResult) {
        onResult(fullTranscript);
      }
    };

    rec.start();
  }, [isListening]);

  const stopListening = useCallback(() => {
    const rec = recognitionRef.current;
    if (rec && isListening) {
      rec.stop();
      setIsListening(false);
    }
  }, [isListening]);

  const toggleListening = useCallback((onResult) => {
    if (isListening) {
      stopListening();
    } else {
      startListening(onResult);
    }
  }, [isListening, startListening, stopListening]);

  const cleanup = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, []);

  return { isListening, initialize, startListening, stopListening, toggleListening, cleanup };
};
